import { reactMount } from '@/lib/reactMount'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { useRef, useState, useEffect } from 'react'
import { Button } from '@/components/Button'

function makeNotificationId (n) {
  const payload = [
    n.Title.trim(),
    new Date(n.ShowFrom).toISOString(),
    new Date(n.ShowUntil).toISOString()
  ].join('|')

  // FNV-1a hash (32-bit)
  let hash = 0x811c9dc5
  for (let i = 0; i < payload.length; i++) {
    hash ^= payload.charCodeAt(i)
    hash = (hash * 0x01000193) >>> 0 // multiply & keep unsigned
  }

  // Convert to base36 string for compactness
  return 'notif_' + hash.toString(36)
}

function getInlineToggleHtml (expanded) {
  const label = expanded ? 'Show less' : 'Show more'
  const ariaExpanded = expanded ? 'true' : 'false'

  return ` <button type="button" data-banner-expand-toggle="true" aria-expanded="${ariaExpanded}" class="appearance-none !outline-none overflow-hidden select-none relative inline-flex -tracking-[0.025em] justify-center items-center font-extrabold transition-all duration-200 ease-in-out focus:outline-none whitespace-normal text-center break-words bg-transparent text-[--theme-text] hover:text-[--theme-text-hover] before:content-[''] before:absolute before:w-full before:transition-all before:duration-200 before:left-0 before:bottom-0 before:border-b-2 before:border-[--theme-text] hover:before:w-0 !px-0 !rounded-none !py-2 !pt-0 !pb-0.5 text-sm -mb-1"><span class="relative">${label}</span></button>`
}

function appendInlineToggleToLastElement (html, expanded) {
  const toggleHtml = getInlineToggleHtml(expanded)
  const trimmedHtml = html.trim()
  const finalClosingTag = trimmedHtml.match(/<\/([a-z][\w:-]*)>\s*$/i)

  if (!finalClosingTag) {
    return `${html}${toggleHtml}`
  }

  const insertIndex = trimmedHtml.lastIndexOf(finalClosingTag[0])

  return `${trimmedHtml.slice(0, insertIndex)}${toggleHtml}${trimmedHtml.slice(insertIndex)}`
}

// Custom hook for sessionStorage-backed state
function useSessionStorage (defaultValue, notificationObject) {
  // A unique key for this banner in sessionStorage
  const BANNER_SESSION_KEY = makeNotificationId(notificationObject)

  const [state, setState] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = window.sessionStorage.getItem(BANNER_SESSION_KEY)
      if (stored !== null) {
        return JSON.parse(stored)
      }
    }
    return defaultValue
  })

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(BANNER_SESSION_KEY, JSON.stringify(state))
    }
  }, [state])

  return [state, setState]
}

const bannerBlock = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco))
  umbraco ||= umbracoRef.current
  const NotificationLevel = umbraco.NotificationLevel || 'default'
  const [visible, setVisible] = useSessionStorage(true, umbraco)
  const [expanded, setExpanded] = useState(false)

  const handleDismiss = () => {
    setVisible(false)
  }

  const handleDescriptionClick = (event) => {
    const toggleButton = event.target.closest('[data-banner-expand-toggle]')

    if (!toggleButton) return

    event.preventDefault()
    setExpanded((open) => !open)
  }

  if (!visible) return null

  return (
    <div className={`relative isolate bg-[--notification-bg] ${NotificationLevel === 'warning' ? '!bg-green-1' : ''}`} data-emergency-level={NotificationLevel}>
      <div className='mx-auto w-full justify-between items-start max-w-[1440px] flex xl:pr-[calc(((100vw_-_1216px)_/_2)_-_24px)] custom-1450:pr-[75px]'>
        <div className='col-span-full space-y-2 items-start justify-center px-4 py-2 sm:px-6 lg:px-8 xl:pr-0 max-sm:pr-0'>
          <div className='space-y-2 text-[--notification-text] pt-0.5'>
            {umbraco.Title && (
              <h3 className='text-sm font-extrabold xl:pr-5 shrink-0 text-[--notification-title] -mt-1'>{umbraco.Title}</h3>
            )}
            {umbraco.Description && (
              <div
                className='text-sm text-[--notification-text] max-w-[1294px] !mt-[5px]'
                onClick={handleDescriptionClick}
                dangerouslySetInnerHTML={{
                  __html: umbraco.ExpandedDescription && !expanded
                    ? appendInlineToggleToLastElement(umbraco.Description, expanded)
                    : umbraco.Description
                }}
              />
            )}
            {umbraco.ExpandedDescription && (
              <div
                className={`grid !mt-0 ease-out duration-300 ${expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
              >
                <div
                  className={`overflow-hidden ease-in-out duration-500 ${expanded ? 'opacity-100 delay-100' : 'opacity-0 delay-0'}`}
                >
                  <div
                    className='text-sm text-[--notification-text] max-w-[1294px] !mt-0'
                    onClick={handleDescriptionClick}
                    dangerouslySetInnerHTML={{
                      __html: expanded
                        ? appendInlineToggleToLastElement(umbraco.ExpandedDescription, expanded)
                        : umbraco.ExpandedDescription
                    }}
                  />
                </div>
              </div>
            )}

          </div>

          {umbraco.Button?.Url && (
            <Button
              variant='link'
              href={umbraco.Button.Url}
              target={umbraco.Button?.Target || null}
              className='!pt-0 !pb-1 text-sm justify-start inline-flex !text-left appearance-none !outline-none -tracking-[0.025em] !mt-1.5'
              componentName={umbraco.Button.Url?.includes('#apply-dialog') ? 'Banner Block' : null}
            >
              {umbraco.Button.Name}
            </Button>
          )}
        </div>
        <button
          type='button'
          className='shrink-0 p-3 max-sm:pr-2 lg:mr-3 xl:mr-0 custom-1450:mr-5 focus-visible:outline-offset-[-4px]'
          onClick={handleDismiss}
        >
          <span className='sr-only'>Dismiss</span>
          <XMarkIcon aria-hidden='true' className='size-6 text-[--notification-title]' />
        </button>
      </div>

    </div>
  )
}

reactMount('bannerBlock', bannerBlock)
